import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../../components/feature/LanguageSwitcher';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/products' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? 'bg-primary-dark/95 backdrop-blur-md shadow-lg shadow-black/20' : 'bg-transparent'
      }`}>
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center justify-end h-16 md:h-20 gap-2">

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-2 ml-auto mr-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 font-medium text-lg tracking-wide transition-all duration-300 cursor-pointer ${
                    location.pathname === link.path ? 'text-gold' : 'text-white/75 hover:text-white'
                  }`}
                >
                  {link.name}
                  {location.pathname === link.path && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-gold rounded-full" />
                  )}
                </Link>
              ))}
            </div>

            {/* Right side links */}
            <div className="hidden lg:flex items-center gap-3">

              {/* Language switcher */}
              <div className="[&_button]:!bg-transparent [&_button]:!border-white/20 [&_button]:!text-white/60 [&_button]:!shadow-none [&_button]:hover:!text-white">
                <LanguageSwitcher />
              </div>

              {/* Company Profile PDF Download */}
              <a
                href="/ALEBEL ENERGY PROFILE.pdf"
                download
                className="group flex items-center gap-2 px-4 py-2 rounded-lg border border-gold/30 hover:border-gold/60 bg-gold/5 hover:bg-gold/10 transition-all duration-300"
              >
                <i className="ri-file-download-line text-gold text-base flex-shrink-0" />
                <div>
                  <div className="text-white/85 text-sm font-semibold leading-tight group-hover:text-white transition-colors">
                    {t('nav.companyProfile') || 'Company Profile'}
                  </div>
                  <div className="text-white/35 text-xs leading-tight">Download PDF</div>
                </div>
              </a>

              {/* Alebel Investment */}
              <a
                href="https://www.alebel.com"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2.5 px-4 py-2 rounded-lg border border-white/10 hover:border-gold/40 bg-white/5 hover:bg-white/8 transition-all duration-300"
              >
                <i className="ri-building-line text-gold text-base flex-shrink-0" />
                <div>
                  <div className="text-white/85 text-sm font-semibold leading-tight group-hover:text-white transition-colors">
                    {t('nav.investment')}
                  </div>
                  <div className="text-white/35 text-xs leading-tight">{t('nav.investmentDesc')}</div>
                </div>
                <i className="ri-external-link-line text-white/20 text-xs ml-1 group-hover:text-gold/50 transition-colors" />
              </a>

            </div>

            {/* Mobile toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-white/5 transition-colors"
            >
              <i className={`text-xl text-white ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`} />
            </button>
          </div>
        </div>
        {isScrolled && <div className="h-px bg-gradient-to-r from-transparent via-gold/20 to-transparent" />}
      </nav>

      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile panel */}
      <div className={`fixed top-0 ${isRTL ? 'left-0' : 'right-0'} h-full w-[80%] max-w-[300px] bg-primary-dark/98 backdrop-blur-xl z-50 lg:hidden transform transition-transform duration-500 border-l border-gold/10 ${
        isMobileMenuOpen ? 'translate-x-0' : isRTL ? '-translate-x-full' : 'translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-6 py-5 border-b border-gold/10">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="/logo-energy.png" alt="Alebel Energy" className="h-9 w-auto"
                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            </Link>
            <button onClick={() => setIsMobileMenuOpen(false)} className="w-9 h-9 flex items-center justify-center rounded-lg hover:bg-white/5">
              <i className="ri-close-line text-xl text-white/70" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto py-4 px-3">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path} onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-3.5 font-medium rounded-lg text-sm mb-1 transition-all ${
                  location.pathname === link.path ? 'text-gold bg-gold/10' : 'text-white/65 hover:text-white hover:bg-white/5'
                }`}>
                {location.pathname === link.path && <span className="w-1 h-1 rounded-full bg-gold mr-3" />}
                {link.name}
              </Link>
            ))}

            {/* Mobile — Company Profile Download */}
            <a
              href="/company-profile.pdf"
              download
              className="flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm mb-1 text-gold border border-gold/20 bg-gold/5 hover:bg-gold/10 transition-all mt-3"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-file-download-line text-base" />
              {t('nav.companyProfile') || 'Company Profile'}
            </a>

            {/* Mobile — Alebel Investment */}
            <a
              href="https://www.alebel.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-4 py-3.5 rounded-lg text-sm mb-1 text-white/65 hover:text-white hover:bg-white/5 transition-all"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <i className="ri-building-line text-gold text-base" />
              {t('nav.investment')}
            </a>

            <div className="mt-4 pt-4 border-t border-gold/10 px-4 [&_button]:!bg-transparent [&_button]:!border-white/20 [&_button]:!text-white/60 [&_button]:!shadow-none">
              <LanguageSwitcher />
            </div>
          </div>

          <div className="px-6 py-5 border-t border-gold/10">
            <p className="text-white/25 text-[10px] tracking-widest uppercase mb-2">Get in Touch</p>
            <p className="text-gold text-sm font-medium">+971 4 888 6288</p>
            <p className="text-white/40 text-xs mt-1">info@alebelenergy.com</p>
          </div>
        </div>
      </div>
    </>
  );
}
